import { onMounted, onUnmounted, ref } from "vue";
import { useState } from "@/store/vuex-composition-mappers";

const isInIframe = () => {
    try {
        return window !== window.parent;
    } catch (e) {
        // If we can't access parent, we're likely in an iframe
        return true;
    }
};

const getViewportHeight = () => {
    if (isInIframe()) {
        try {
            const iframe = window.frameElement;
            if (iframe) {
                return iframe.offsetHeight;
            }
        } catch (e) {
            // Fallback to window height if we can't access iframe
        }
    }
    return window.innerHeight;
};

function isViewportScrollable() {
    const getViewportHeight = () => {
        if (isInIframe()) {
            try {
                const iframe = window.frameElement;
                if (iframe) {
                    return iframe.offsetHeight;
                }
            } catch (e) {
                // Fallback to window height if we can't access iframe
            }
        }
        return window.innerHeight;
    };

    const viewportHeight = getViewportHeight();
    return document.body.scrollHeight > viewportHeight;
}

export function useDragAndDrop({ onBlockDropped, onToolDropped }) {
    const { preview } = useState("shared/view", ["preview"]);
    const insertPosition = ref(null);
    const isDragging = ref(false);
    const baseDropIndicatorHeight = 80;
    const originalBlockDimensions = ref(new Map());

    function resetDropState() {
        isDragging.value = false;
        const dropIndicator = document
            .querySelector("[data-dnd-container='true']")
            .querySelector("[data-dnd='drop-indicator']");
        dropIndicator.classList.add("hidden");
        dropIndicator.style.height = `${baseDropIndicatorHeight}px`;

        removePaddingFromElements();
        originalBlockDimensions.value.clear();
    }

    // Remove padding from all blocks (padding is added during drag over to make place for the drop indicator)
    function removePaddingFromElements() {
        const blocks = Array.from(
            document
                .querySelector("[data-dnd-container='true']")
                .querySelectorAll("[data-dnd='section']")
        );

        blocks.forEach((block) => {
            block.style.paddingBottom = "";
            block.style.paddingTop = "";
        });
    }

    function cacheOriginalBlockDimensions() {
        // Only cache dimensions if viewport is not scrollable to avoid flickering when dimensions change due added padding
        if (isViewportScrollable()) {
            originalBlockDimensions.value.clear();
            return;
        }

        const blocks = Array.from(
            document
                .querySelector("[data-dnd-container='true']")
                .querySelectorAll("[data-dnd='section']")
        );

        originalBlockDimensions.value.clear();
        blocks.forEach((block, index) => {
            const rect = block.getBoundingClientRect();
            originalBlockDimensions.value.set(index, {
                top: rect.top,
                height: rect.height,
                bottom: rect.bottom,
            });
        });
    }

    function handleDragOver(e) {
        e.preventDefault();
        if (!e.dataTransfer.types.includes("new-easypage-section")) return;

        // Cache original dimensions on first drag over only if viewport is not scrollable
        if (!isDragging.value) {
            cacheOriginalBlockDimensions();
        }

        isDragging.value = true;

        const dropIndicator = document
            .querySelector("[data-dnd-container='true']")
            .querySelector("[data-dnd='drop-indicator']");
        const rect = document.body.getBoundingClientRect();
        const y = e.clientY - rect.top;

        const blocks = Array.from(
            document
                .querySelector("[data-dnd-container='true']")
                .querySelectorAll("[data-dnd='section']")
        );
        let insertBeforeElement = null;

        for (let i = 0; i < blocks.length; i++) {
            let blockMidY;

            // Use cached dimensions only if viewport is not scrollable and cache exists
            if (!isViewportScrollable() && originalBlockDimensions.value.has(i)) {
                const cachedDimensions = originalBlockDimensions.value.get(i);
                blockMidY = cachedDimensions.top + cachedDimensions.height / 2 - rect.top;
            } else {
                const blockRect = blocks[i].getBoundingClientRect();
                blockMidY = blockRect.top + blockRect.height / 2 - rect.top;
            }

            if (y < blockMidY) {
                insertBeforeElement = blocks[i];
                insertPosition.value = i;
                break;
            }
        }

        positionDropIndicator(insertBeforeElement, blocks, rect, dropIndicator);
    }

    function positionDropIndicator(insertBeforeElement, blocks, rect, dropIndicator) {
        removePaddingFromElements();

        const viewportHeight = getViewportHeight();

        if (insertBeforeElement) {
            const targetRect = insertBeforeElement.getBoundingClientRect();

            dropIndicator.style.top = `${targetRect.top - rect.top}px`;
            dropIndicator.style.height = `${baseDropIndicatorHeight}px`;

            insertBeforeElement.style.paddingTop = `${baseDropIndicatorHeight}px`;
        } else {
            insertPosition.value = blocks.length;
            const lastBlock = blocks[blocks.length - 1];
            if (lastBlock) {
                let lastBlockBottom;

                if (
                    !isViewportScrollable() &&
                    originalBlockDimensions.value.has(blocks.length - 1)
                ) {
                    const cachedDimensions = originalBlockDimensions.value.get(blocks.length - 1);
                    lastBlockBottom = cachedDimensions.bottom;
                } else {
                    const lastBlockRect = lastBlock.getBoundingClientRect();
                    lastBlockBottom = lastBlockRect.bottom;
                }

                dropIndicator.style.top = `${lastBlockBottom - rect.top}px`;

                // Handle remaining space in viewport when dragging over the last block to fill with the drop indicator
                const remainingHeight = viewportHeight - (lastBlockBottom - rect.top);
                dropIndicator.style.height = `${Math.max(remainingHeight, baseDropIndicatorHeight)}px`;
                lastBlock.style.paddingBottom = `${Math.max(remainingHeight, baseDropIndicatorHeight)}px`;
            } else {
                // No sections exist - take full viewport height
                dropIndicator.style.top = "0px";
                dropIndicator.style.height = `${viewportHeight}px`;
            }
        }

        dropIndicator.classList.remove("hidden");
    }

    function handleDrop(e) {
        e.preventDefault();
        if (!e.dataTransfer.types.includes("new-easypage-section")) return;

        const { type, data } = JSON.parse(e.dataTransfer.getData("new-easypage-section"));

        if (type === "block") {
            onBlockDropped({
                block: data,
                position: insertPosition.value,
            });
        } else if (type === "tool") {
            onToolDropped({
                project: data,
                position: insertPosition.value,
            });
        }

        resetDropState();
    }

    function handleDragEnd() {
        resetDropState();
    }

    function handleDragLeave(e) {
        if (isInIframe()) {
            const iframe = window.frameElement;
            if (iframe) {
                const iframeRect = iframe.getBoundingClientRect();

                // Check if mouse position is outside iframe bounds in parent window coordinates
                if (
                    e.clientX <= 0 ||
                    e.clientY <= 0 ||
                    e.clientX >= iframeRect.width ||
                    e.clientY >= iframeRect.height
                ) {
                    resetDropState();
                }
            }
        } else {
            if (
                e.clientY <= 0 ||
                e.clientX <= 0 ||
                e.clientX >= window.innerWidth ||
                e.clientY >= window.innerHeight
            ) {
                resetDropState();
            }
        }
    }

    function handleParentDragOver() {
        isDragging.value = true;
    }

    function handleParentDragLeave(e) {
        if (!isInIframe() || !isDragging.value) return;

        const iframe = window.frameElement;
        if (iframe) {
            const iframeRect = iframe.getBoundingClientRect();

            if (
                e.clientX < iframeRect.left ||
                e.clientY < iframeRect.top ||
                e.clientX > iframeRect.right ||
                e.clientY > iframeRect.bottom
            ) {
                resetDropState();
            }
        }
    }

    onMounted(() => {
        if (!preview.value) return;

        document.body.addEventListener("dragover", handleDragOver);
        document.body.addEventListener("drop", handleDrop);
        document.body.addEventListener("dragend", handleDragEnd);
        document.body.addEventListener("dragleave", handleDragLeave);

        // If we're in an iframe, also listen to parent window events
        if (isInIframe()) {
            try {
                window.parent.document.addEventListener("dragover", handleParentDragOver);
                window.parent.document.addEventListener("dragleave", handleParentDragLeave);
            } catch (e) {
                console.warn("Cannot access parent window events due to cross-origin restrictions");
            }
        }
    });

    onUnmounted(() => {
        if (!preview.value) return;

        document.body.removeEventListener("dragover", handleDragOver);
        document.body.removeEventListener("drop", handleDrop);
        document.body.removeEventListener("dragend", handleDragEnd);
        document.body.removeEventListener("dragleave", handleDragLeave);

        if (isInIframe()) {
            try {
                window.parent.document.removeEventListener("dragover", handleParentDragOver);
                window.parent.document.removeEventListener("dragleave", handleParentDragLeave);
            } catch (e) {
                // Cross-origin restrictions might prevent this
            }
        }
    });

    return {
        isDragging,
    };
}
